import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import {
  OUTSTANDING_PLEDGES_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/outstandingPledgesColumnsConfig';
import { currencyFormatter, compactCurrency } from '../utils/reportingUtils';

const axios = require('axios').default;

const STATUS_BADGES = {
  active: { background: '#ecfdf3', color: '#027a48' },
  delinquent: { background: '#fef3f2', color: '#b42318' },
  partially_paid: { background: '#fffaeb', color: '#b54708' },
  fulfilled: { background: '#eff8ff', color: '#175cd3' },
  written_off: { background: '#f2f4f7', color: '#344054' },
};

const buildFilterGroups = (filterOptions) => {
  const groups = [];
  const opts = filterOptions || {};

  if (opts.statuses && opts.statuses.length > 0) {
    groups.push({
      label: 'Status',
      filters: [{ id: 'status', label: 'Status', type: 'checkbox', options: opts.statuses }],
    });
  }
  if (opts.funds && opts.funds.length > 0) {
    groups.push({
      label: 'Fund',
      filters: [{ id: 'fund_id', label: 'Fund', type: 'checkbox', options: opts.funds }],
    });
  }
  if (opts.giving_campaigns && opts.giving_campaigns.length > 0) {
    groups.push({
      label: 'Campaign',
      filters: [{ id: 'giving_campaign_id', label: 'Giving Campaign', type: 'checkbox', options: opts.giving_campaigns }],
    });
  }
  groups.push({
    label: 'Aging',
    filters: [{ id: 'overdue_only', label: 'Overdue', type: 'checkbox', options: [{ value: '1', label: 'Overdue pledges only' }] }],
  });

  return groups;
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.status && activeFilters.status.length > 0) params.status = activeFilters.status;
  if (activeFilters.fund_id && activeFilters.fund_id.length > 0) params.fund_id = activeFilters.fund_id;
  if (activeFilters.giving_campaign_id && activeFilters.giving_campaign_id.length > 0) {
    params.giving_campaign_id = activeFilters.giving_campaign_id;
  }
  if (activeFilters.overdue_only && activeFilters.overdue_only.length > 0) params.overdue_only = true;
  return params;
};

const OUTSTANDING_PLEDGES_CONFIG = {
  pageTitle: 'Outstanding Pledges',
  pageSubtitle: 'Open pledges with aging, balance, and next payment due.',
  sidebarKey: 'donations_outstanding_pledges',
  defaultPreset: 'year_to_date',
  // Snapshot of all open pledges — date filtering would wrongly hide older still-open pledges.
  showDatePicker: false,
  dateModeOptions: [{ key: 'order_date', label: 'Order Date' }],

  columns: OUTSTANDING_PLEDGES_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingOutstandingPledgesColumns_${teamId}`,
  firstColumnId: 'pledge_number',
  defaultSortRules: [{ field: 'days_overdue', direction: 'desc' }],

  kpiFromDetailResponse: true,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/donations/outstanding-pledges/data.json`,
  detailResponseKey: 'pledges',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/donations/outstanding-pledges/export`,
  detailErrorMessage: 'Failed to load pledge data.',
  detailTableTitle: 'Outstanding Pledges',
  emptyMessage: 'No outstanding pledges.',
  searchPlaceholder: 'Search pledges...',
  paginationLabel: 'pledges',
  itemLabel: (count) => `pledge${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,
  filterTitle: 'Filter Pledges',
  filterSubtitle: 'Apply filters to narrow outstanding pledges.',

  buildKpiCards: (current) => [
    { label: 'Open Pledges', value: (current.open_pledges || 0).toLocaleString(), current: current.open_pledges || 0 },
    { label: 'Total Pledged', value: compactCurrency(current.total_pledged || 0), current: current.total_pledged || 0 },
    { label: 'Outstanding Balance', value: compactCurrency(current.outstanding_amount || 0), current: current.outstanding_amount || 0 },
    { label: 'Overdue Amount', value: compactCurrency(current.overdue_amount || 0), current: current.overdue_amount || 0 },
    { label: 'Pledges Overdue', value: (current.pledges_overdue || 0).toLocaleString(), current: current.pledges_overdue || 0 },
  ],
  kpiGridColumns: 5,

  renderCellValue: (col, row, { currencyFormatter: cf, formatEventDate, team }) => {
    switch (col.id) {
      case 'pledge_number': return `#${row.pledge_number}`;
      case 'donor_name': {
        const name = row.donor_name || '(No name)';
        if (team?.package?.crm && row.fan_id) {
          return (
            <a href={`/teams/${team.id}/crm/fans/${row.fan_id}`} style={{ color: 'var(--sl-fg-brand, #1982c4)' }}>
              {name}
            </a>
          );
        }
        return name;
      }
      case 'fund_name': return row.fund_name || '—';
      case 'campaign_name': return row.campaign_name || '—';
      case 'status': {
        const badge = STATUS_BADGES[row.status] || STATUS_BADGES.active;
        const label = row.status ? row.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';
        return <span className="badge" style={{ ...badge, fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 12 }}>{label}</span>;
      }
      case 'total_amount': return cf.format(row.total_amount || 0);
      case 'outstanding_amount': return cf.format(row.outstanding_amount || 0);
      case 'overdue_amount':
        return row.overdue_amount > 0
          ? <span style={{ color: '#b42318', fontWeight: 600 }}>{cf.format(row.overdue_amount)}</span>
          : cf.format(0);
      case 'next_due_date': return <span className="text-nowrap">{row.next_due_date ? formatEventDate(row.next_due_date) : '—'}</span>;
      case 'days_overdue':
        return row.days_overdue > 0
          ? <span style={{ color: '#b42318', fontWeight: 600 }}>{row.days_overdue}</span>
          : '—';
      case 'expected_fulfillment_date': return <span className="text-nowrap">{row.expected_fulfillment_date ? formatEventDate(row.expected_fulfillment_date) : '—'}</span>;
      case 'pledge_date': return <span className="text-nowrap">{row.pledge_date ? formatEventDate(row.pledge_date) : '—'}</span>;
      default: return '—';
    }
  },
};

function ReportingDonationsOutstandingPledgesPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/donations/outstanding-pledges/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch(() => {});
  }, [team && team.id, csrfToken]);

  const config = {
    ...OUTSTANDING_PLEDGES_CONFIG,
    buildFilterGroups: () => buildFilterGroups(filterOptions),
  };

  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={config} />;
}

export default ReportingDonationsOutstandingPledgesPage;
